<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAuthStore } from './stores/auth.ts';
import { useCompanyStore } from './stores/company.ts';
import RouteLoader from './components/RouteLoader.vue';
import NavBar from './components/NavBar.vue';
import DashboardNavBar from './components/dashboard/DashboardNavBar.vue';
import DashboardFooter from './components/dashboard/DashboardFooter.vue';
import LoadingSplash from './components/LoadingSplash.vue';
import TrialExpiredOverlay from './components/TrialExpiredOverlay.vue';
import MandyAssistant from './views/mandy/index.vue';

// Track timeouts for cleanup
let routeChangeTimeout: ReturnType<typeof setTimeout> | null = null;
let loadingTimeout: ReturnType<typeof setTimeout> | null = null;

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const companyStore = useCompanyStore();
const { isAuthenticated, isInitialized, user } = storeToRefs(authStore);
const { company, hasTrialExpired } = storeToRefs(companyStore);

const isDashboardRoute = computed(() => {
  return route.path.startsWith('/dashboard') ||
         route.path.startsWith('/employee') ||
         route.path.startsWith('/account') ||
         route.path.startsWith('/checklists') ||
         route.path.startsWith('/company') ||
         route.path.startsWith('/super-admin')
});

const showNavbar = computed(() => {
  if (route.meta.showNavbar === false) return false;
  return !isDashboardRoute.value;
});

const showDashboardNavbar = computed(() => {
  if (route.meta.showNavbar === false) return false;
  return isDashboardRoute.value;
});

const handleRouteChange = async () => {
  window.scrollTo(0, 0);

  const routeRequiresAuth = route.matched.some(record => record.meta.requiresAuth);

  // Skip auth check for public routes
  if (route.meta.public) return;

  // Handle authenticated routes
  if (routeRequiresAuth) {
    if (!isAuthenticated.value) {
      // If not initialized, wait for initialization
      if (!isInitialized.value) {
        try {
          await authStore.initialize();
        } catch (error) {
          console.error('Auth initialization error:', error);
        }
      }

      // If still not authenticated, redirect to login
      if (!isAuthenticated.value) {
        return router.push({
          name: 'Login',
          query: { redirect: route.fullPath }
        });
      }
    }
  }
};

watch(() => route.path, handleRouteChange);

// Setup router navigation guards
router.beforeEach((to) => {
  // Skip loading animation for root path
  const pathsToExclude = [
    '/',
    '/sign-out-success',
    '/dashboard',
    '/account/overview',
    '/account/profile',
    '/account/password',
    '/account/subscription'
  ];
  const isCompanyRoute = to.path.startsWith('/company');
  if (!pathsToExclude.includes(to.path) && !isCompanyRoute) {
    isRouteChanging.value = true;
    isLoading.value = false;
  }
});

router.afterEach((to) => {
  // Skip loading animation for root path
  if (to.path === '/') {
    isRouteChanging.value = false;
    return;
  }

  // Clear any existing timeout before setting a new one
  if (routeChangeTimeout) {
    clearTimeout(routeChangeTimeout);
  }

  // Small delay to prevent flash of loading for fast route changes
  routeChangeTimeout = setTimeout(() => {
    isRouteChanging.value = false;
    routeChangeTimeout = null;
  }, 300);
});

const isLoading = ref(true);
const isRouteChanging = ref(false);

onMounted(async () => {
  try {
    await authStore.initialize(); // Wait for auth initialization

    // Fetch company data if user is authenticated and has a company
    if (isAuthenticated.value && user.value?.company_id) {
      await companyStore.fetchCompany(user.value.company_id);
    }
  } catch (error) {
    console.error('Auth initialization error:', error);
  } finally {
    await nextTick();

    loadingTimeout = setTimeout(() => {
      isLoading.value = false;
      loadingTimeout = null;
    }, 1500);
  }
});

// Watch for authentication changes to fetch company data
watch(isAuthenticated, async (newValue) => {
  if (newValue && user.value?.company_id) {
    await companyStore.fetchCompany(user.value.company_id);
  }
});

// Cleanup timeouts on component unmount
onBeforeUnmount(() => {
  if (routeChangeTimeout) {
    clearTimeout(routeChangeTimeout);
    routeChangeTimeout = null;
  }

  if (loadingTimeout) {
    clearTimeout(loadingTimeout);
    loadingTimeout = null;
  }
});
</script>

<template>
  <LoadingSplash
    v-if="isLoading && !isRouteChanging"
    :show-logo="false"
  />
  <RouteLoader v-else-if="isRouteChanging" />
  <div v-else class="app-wrapper">
    <MandyAssistant :current-route="route" :current-user="authStore.user" />
    
    <!-- Trial Expired Overlay -->
    <TrialExpiredOverlay 
      v-if="isAuthenticated && company && isDashboardRoute && !route.path.startsWith('/account/subscription')"
      :company="company" 
    />
    
    <div v-if="!isDashboardRoute" class="app-container" :class="{ 'has-navbar': showNavbar }">
      <NavBar v-if="showNavbar" />
      <main class="main-content">
        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </main>
    </div>
    <div v-else class="dashboard-container">
      <DashboardNavBar v-if="showDashboardNavbar" />
      <main class="dashboard-main-content">
        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </main>
      <DashboardFooter />
    </div>
  </div>
</template>
<style>
.app-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex: 1;
}

.dashboard-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.dashboard-main-content {
  flex: 1;
  padding-top: 70px;
  display: flex;
  flex-direction: column;
}
</style>
